import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { usersSelector } from '../redux/users/selectors';
import { useAppDispatch } from '../redux/store';
import axios from 'axios';
import { GET_BASE_URL } from "../constants/apiEndpoints";
import { RootState } from '../redux/store';
import StickerFairyLogo from '../assets/img/StickerFairyLogo.jpg';
import dotsGif from '../assets/img/dots2.gif';
import { fetchMyCollection, fetchUserMenu } from '../redux/lounges/slice';
import '../css/stickerFairy.css';
import Modal from 'react-modal';
import ReactCrop, { type PixelCrop, type PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { cropToRect } from '../utils/imageProcessing';
import { buildStickerFairyPrompt } from '../utils/stickerFairyPrompt';
import { downloadStickerAsset } from '../utils/stickerFairyDownload';
import {
  movePercentCrop,
  normalizePercentCrop,
  percentCropToPixelCrop,
  resizePercentCrop,
  STICKER_FAIRY_DEFAULT_CROP,
} from '../utils/cropControls';



const StickerFairy = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useSelector(usersSelector);
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isImageToStickerLoading, setIsImageToStickerLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [productName, setProductName] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [transparentImageUrl, setTransparentImageUrl] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const dispatch = useAppDispatch();
  const [addCollectionErrorMessage, setAddCollectionErrorMessage] = useState('');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imageToStickerPrompt, setImageToStickerPrompt] = useState('');
  const [imageToStickerUrl, setImageToStickerUrl] = useState('');
  const [imageToStickerThumbnailUrl, setImageToStickerThumbnailUrl] = useState('');
  const [imageToStickerProductName, setImageToStickerProductName] = useState('');
  const [imageToStickerErrorMessage, setImageToStickerErrorMessage] = useState('');
  const [imageToStickerSuccessMessage, setImageToStickerSuccessMessage] = useState('');
  const [addImageToStickerCollectionError, setAddImageToStickerCollectionError] = useState('');
  const [baseTransparentUrl, setBaseTransparentUrl] = useState('');
  const [baseImageToStickerUrl, setBaseImageToStickerUrl] = useState('');
  const [isCropping, setIsCropping] = useState(false);
  const [cropTarget, setCropTarget] = useState<'text' | 'image' | null>(null);
  const [cropSourceUrl, setCropSourceUrl] = useState('');
  const [crop, setCrop] = useState<PercentCrop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [cropError, setCropError] = useState('');
  const [textCropNotice, setTextCropNotice] = useState('');
  const [imageCropNotice, setImageCropNotice] = useState('');
  const cropImageRef = useRef<HTMLImageElement | null>(null);

  const handleDownload = useCallback(async (fileUrl: string, fileName: string) => {
    await downloadStickerAsset(fileUrl, fileName);
  }, []);

  const myCollectionItem = useSelector((state: RootState) => {
    const item = state.lounges.myCollectionItem;
    return Array.isArray(item) ? item[0] : item;
  });

  const creditBalance = myCollectionItem ? (Array.isArray(myCollectionItem) ? myCollectionItem[0]?.credit_balance || 0 : myCollectionItem.credit_balance || 0) : 0;

  useEffect(() => {
    Modal.setAppElement('#root');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsCheckingAccess(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/disneyland/login', { state: { returnTo: '/disneyland/StickerFairy', source: 'StickerFairy' } });
        return;
      }

      const club333 = localStorage.getItem('club333');
      const loungeland = localStorage.getItem('loungeland');
      if (club333 === 'true' || loungeland === 'true') {
        setHasAccess(true);
        await dispatch(fetchMyCollection());
        return;
      }

      try {
        // Access flags are set elsewhere (LeftLounge/LandLounge). If a user lands here
        // before that menu fetch completes, localStorage may be empty and they'd be blocked.
        // Refresh rights from the server to avoid false negatives.
        const loginuserid = localStorage.getItem('user_id') || '';
        const menu = await dispatch(fetchUserMenu({ loginuserid })).unwrap();
        const items = Array.isArray(menu) ? menu : [];

        const hasLoungeLand = items.some((item: any) => item?.rights_id == '13');
        const hasClub333 = items.some((item: any) => item?.rights_id == '14');

        localStorage.setItem('loungeland', hasLoungeLand ? 'true' : '');
        localStorage.setItem('club333', hasClub333 ? 'true' : '');

        const allowed = hasLoungeLand || hasClub333;
        setHasAccess(allowed);
        if (allowed) {
          await dispatch(fetchMyCollection());
        }
      } catch (error) {
        console.error('Error checking StickerFairy access:', error);
        setHasAccess(false);
      }
    };
  
    fetchData()
      .catch((error) => console.error('StickerFairy access check failed:', error))
      .finally(() => setIsCheckingAccess(false));
  }, [navigate, dispatch]);
    
  const checkCreditBalance = () => {
    if (creditBalance < 10) {
      throw new Error('Insufficient credits. You need at least 10 credits to complete this transaction.');
    }
  };

  const updateCreditBalance = async () => {
    try {
      await dispatch(fetchMyCollection());
      // The state will be updated by the Redux action, so no need to set it here
    } catch (error) {
      console.error('Error updating credit balance:', error);
    }
  };
  
  

  const processStickerGeneration = async () => {
    const token = localStorage.getItem('token');

    try {
      checkCreditBalance();

      const response = await fetch(GET_BASE_URL + "/api/v1/processStickerGen", {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      await updateCreditBalance(); // Return the updated credit balance
  } catch (error) {
    console.error('Error processing sticker generation:', error);
    if (error instanceof Error) {
      setErrorMessage(error.message);
    } else {
      setErrorMessage('An unexpected error occurred.');
    }
  }
};

  const generateRandomId = () => {
    const randomNumber = Math.floor(Math.random() * 90000) + 10000; // Generate a random number between 10000 and 99999
    return randomNumber.toString(); // Convert the number to a string
  };

  const generateThumbnail = (imageUrl: string, setThumbnailUrl: (url: string) => void) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const maxHeight = 150;
      const scale = maxHeight / img.height;
      const targetHeight = maxHeight;
      const targetWidth = Math.max(1, Math.round(img.width * scale));

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      const thumbnail = canvas.toDataURL('image/png');
      setThumbnailUrl(thumbnail);
    };

    img.src = imageUrl;
  };
  
  const applyPostProcess = async (target: 'text' | 'image', url: string) => {
    const setUrl = target === 'text' ? setTransparentImageUrl : setImageToStickerUrl;
    const setThumb = target === 'text' ? setThumbnailUrl : setImageToStickerThumbnailUrl;
    const setNotice = target === 'text' ? setTextCropNotice : setImageCropNotice;

    const finalUrl = url;
    setNotice('');

    setUrl(finalUrl);
    generateThumbnail(finalUrl, setThumb);

    return finalUrl;
  };

  const handleResetCrop = (target: 'text' | 'image') => {
    if (target === 'text' && baseTransparentUrl) {
      setTransparentImageUrl(baseTransparentUrl);
      generateThumbnail(baseTransparentUrl, setThumbnailUrl);
      setTextCropNotice('');
    }

    if (target === 'image' && baseImageToStickerUrl) {
      setImageToStickerUrl(baseImageToStickerUrl);
      generateThumbnail(baseImageToStickerUrl, setImageToStickerThumbnailUrl);
      setImageCropNotice('');
    }
  };

  const resetCropState = () => {
    setCrop(undefined);
    setCompletedCrop(null);
    setCropError('');
  };

  const closeCropModal = () => {
    setIsCropping(false);
    setCropTarget(null);
    setCropSourceUrl('');
    resetCropState();
  };

  const updateCropSelection = useCallback((nextCrop: PercentCrop) => {
    const normalizedCrop = normalizePercentCrop(nextCrop);
    setCrop(normalizedCrop);
    setCropError('');

    const image = cropImageRef.current;
    if (!image) {
      setCompletedCrop(null);
      return;
    }

    setCompletedCrop(percentCropToPixelCrop(normalizedCrop, image.width, image.height));
  }, []);

  const openCropModal = (target: 'text' | 'image', url: string) => {
    if (!url) {
      return;
    }

    setCropTarget(target);
    setCropSourceUrl(url);
    setIsCropping(true);
    setCrop(STICKER_FAIRY_DEFAULT_CROP);
    setCompletedCrop(null);
    setCropError('');
  };

  const onCropChange = (_pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    updateCropSelection(percentCrop);
  };

  const onCropComplete = (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    setCrop(normalizePercentCrop(percentCrop));
    if (pixelCrop.width && pixelCrop.height) {
      setCompletedCrop(pixelCrop);
    } else {
      setCompletedCrop(null);
    }
  };

  const onCropImageLoad = () => {
    updateCropSelection(crop || STICKER_FAIRY_DEFAULT_CROP);
  };

  const handleCropMove = (deltaX: number, deltaY: number) => {
    updateCropSelection(movePercentCrop(crop, deltaX, deltaY));
  };

  const handleCropResize = (delta: number) => {
    updateCropSelection(resizePercentCrop(crop, delta));
  };

  const handleCropResetSelection = () => {
    updateCropSelection(STICKER_FAIRY_DEFAULT_CROP);
  };

  const handleCropApply = async () => {
    if (!cropTarget) {
      return;
    }

    const activeCrop = completedCrop || (
      crop && cropImageRef.current
        ? percentCropToPixelCrop(crop, cropImageRef.current.width, cropImageRef.current.height)
        : null
    );

    if (!activeCrop || activeCrop.width <= 0 || activeCrop.height <= 0) {
      setCropError('Select an area to crop.');
      return;
    }

    try {
      const newUrl = await cropToRect(cropSourceUrl, activeCrop, crop);

      if (cropTarget === 'text') {
        setTransparentImageUrl(newUrl);
        generateThumbnail(newUrl, setThumbnailUrl);
        setTextCropNotice('');
      }

      if (cropTarget === 'image') {
        setImageToStickerUrl(newUrl);
        generateThumbnail(newUrl, setImageToStickerThumbnailUrl);
        setImageCropNotice('');
      }

      closeCropModal();
    } catch (error) {
      console.error('Manual crop failed:', error);
      setCropError('Cropping unavailable for this image.');

      if (cropTarget === 'text') {
        setTextCropNotice('Cropping unavailable for this image.');
      }

      if (cropTarget === 'image') {
        setImageCropNotice('Cropping unavailable for this image.');
      }
    }
  };

  useEffect(() => {
    const reapply = async () => {
      if (!baseTransparentUrl && !baseImageToStickerUrl) {
        return;
      }

      if (baseTransparentUrl) {
        setTransparentImageUrl(baseTransparentUrl);
        generateThumbnail(baseTransparentUrl, setThumbnailUrl);
        setTextCropNotice('');
      }

      if (baseImageToStickerUrl) {
        setImageToStickerUrl(baseImageToStickerUrl);
        generateThumbnail(baseImageToStickerUrl, setImageToStickerThumbnailUrl);
        setImageCropNotice('');
      }
    };

    reapply().catch((error) => console.error('Image reapply failed:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTransparentUrl, baseImageToStickerUrl]);


  if (isCheckingAccess) {
    return (
      <div className="sticker-fairy-page">
        <div className="loading-container">
          <img src={dotsGif} alt="Loading" />
          <span>Checking access...</span>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div>
        <h2>StickerFairy is currently for Club 333 and Loungeland members only</h2>
        <p>We are working to make it available to all users soon!</p>
      </div>
    );
  }

  

  const addStickerFairyToCollection = async () => {
    try {
      setIsLoading(true);
      setAddCollectionErrorMessage('');
      setSuccessMessage('');
      await dispatch(fetchMyCollection()); // Fetch the latest collection to update the credit balance
      const updatedCreditBalance = myCollectionItem ? (Array.isArray(myCollectionItem) ? myCollectionItem[0]?.credit_balance || 0 : myCollectionItem.credit_balance || 0) : 0;
      if (updatedCreditBalance < 10) {
        throw new Error('Insufficient credits. You need at least 10 credits to complete this transaction.');
      }
      const token = localStorage.getItem('token');
      const productId = generateRandomId();
      const response = await axios.post(
        GET_BASE_URL + "/api/v1/addStickerFairytoCollection",
        {
          product_id: productId, 
          product_name: productName, 
          product_image: thumbnailUrl, // Thumbnail image URL
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (response.status === 200) {
        console.log('Sticker Fairy added to collection successfully');
        setSuccessMessage('Your Sticker has been added to your collection. To add duplicates, press Add to Collection button above again!');
      } else {
        throw new Error('Failed to add Sticker Fairy to collection');
      }
    } catch (error) {
      console.error('Error adding Sticker Fairy to collection:', error);
      if (error instanceof Error) {
        setAddCollectionErrorMessage(error.message);
      } else {
        setAddCollectionErrorMessage('An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  
  

  const generateImage = async (prompt: string): Promise<{ original: string, transparent: string }> => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const modifiedPrompt = buildStickerFairyPrompt(prompt);
      const response = await axios.post(
        'https://afggnbdnuorljwlnzova.supabase.co/functions/v1/gemini-text-to-sticker',
        {
          prompt: modifiedPrompt,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFmZ2duYmRudW9ybGp3bG56b3ZhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzM2MjU4MDIsImV4cCI6MjA0OTIwMTgwMn0.lXJjCm8Mmm5pbCANGrMhX0rC08A62l2QxNPx0ybbHu0',
          },
        }
      );
      setIsLoading(false);

      const prediction = response.data;
      console.log('Prediction:', prediction);

      // The Supabase Edge Function can return HTTP 200 with a body
      // that has no image output (e.g. Gemini refused the prompt or
      // returned no candidates). Surface whatever message is in the
      // response so the user knows WHY no sticker came back instead
      // of a vague "No image generated."
      if (!prediction || !Array.isArray(prediction.output) || !prediction.output[0]) {
        const serverDetail =
          (typeof prediction === 'string' && prediction) ||
          prediction?.error ||
          prediction?.message ||
          prediction?.detail ||
          prediction?.geminiError ||
          (typeof prediction?.geminiResponse === 'string'
            ? prediction.geminiResponse
            : prediction?.geminiResponse?.error?.message) ||
          (prediction
            ? `Unexpected response from sticker service: ${JSON.stringify(prediction).slice(0, 300)}`
            : 'No response from sticker service.');
        console.error('Gemini text-to-sticker failed:', prediction);
        setErrorMessage(String(serverDetail));
        setBaseTransparentUrl('');
        setTextCropNotice('');
        return { original: '', transparent: '' };
      }

      // Extract the image URLs from the prediction object
      const originalImageUrl = prediction.output[0];
      const transparentImageUrl = prediction.output[1];

      console.log('Original Image URL:', originalImageUrl);
      console.log('Transparent Image URL:', transparentImageUrl);

      setOriginalImageUrl(originalImageUrl);
      setBaseTransparentUrl(transparentImageUrl);
      const finalTransparent = await applyPostProcess('text', transparentImageUrl);

      return { original: originalImageUrl, transparent: finalTransparent };
    } catch (error) {
      setIsLoading(false);
      console.error('Error generating image:', error);
      if (axios.isAxiosError(error)) {
        const errData = error.response?.data as any;
        // Surface the most specific message available — including
        // nested Gemini error structures.
        const serverMessage =
          (typeof errData === 'string' && errData) ||
          errData?.error ||
          errData?.message ||
          errData?.detail ||
          errData?.geminiError ||
          (typeof errData?.geminiResponse === 'string'
            ? errData.geminiResponse
            : errData?.geminiResponse?.error?.message) ||
          error.message;
        const status = error.response?.status;
        const prefix = status ? `(HTTP ${status}) ` : '';
        setErrorMessage(
          serverMessage
            ? `${prefix}${serverMessage}`
            : `${prefix}An unexpected error occurred.`
        );
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
      setBaseTransparentUrl('');
      setTextCropNotice('');
      return { original: '', transparent: '' };
    }
  };
  
  
  const handleGenerateImage = async () => {
    try {
      setAddCollectionErrorMessage('');
      setSuccessMessage('');
      setIsLoading(true);
      await dispatch(fetchMyCollection());
      // Assuming myCollectionItem is updated by the fetchMyCollection action
      const newCreditBalance = myCollectionItem ? (Array.isArray(myCollectionItem) ? myCollectionItem[0]?.credit_balance || 0 : myCollectionItem.credit_balance || 0) : 0;
      if (newCreditBalance < 10) {
        setErrorMessage('Insufficient credits. You need at least 10 credits to complete this transaction.');
        setIsLoading(false);
        return;
      }
      await processStickerGeneration();
      const { original, transparent } = await generateImage(prompt);
      setOriginalImageUrl(original);
      setTransparentImageUrl(transparent);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to process sticker generation or generate image:', error);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    }
  };


  const generateImageToSticker = async (uploadedImage: File | null, prompt: string): Promise<{ original: string, transparent: string }> => {
    setIsImageToStickerLoading(true);
    setImageToStickerErrorMessage('');

    try {
      if (!uploadedImage) {
        setIsImageToStickerLoading(false);
        setImageToStickerErrorMessage('No image file selected.');
        return { original: '', transparent: '' };
      }

      const allowedFileTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!allowedFileTypes.includes(uploadedImage.type)) {
        setIsImageToStickerLoading(false);
        setImageToStickerErrorMessage('Unsupported image file type.');
        return { original: '', transparent: '' };
      }

      const modifiedPrompt = buildStickerFairyPrompt(prompt);

      const formData = new FormData();
      formData.append('uploadedImage', uploadedImage, uploadedImage.name);
      formData.append('prompt', modifiedPrompt);

      const response = await axios.post(
        'https://afggnbdnuorljwlnzova.supabase.co/functions/v1/gemini-image-to-sticker',
        formData,
        {
          headers: {
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFmZ2duYmRudW9ybGp3bG56b3ZhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzM2MjU4MDIsImV4cCI6MjA0OTIwMTgwMn0.lXJjCm8Mmm5pbCANGrMhX0rC08A62l2QxNPx0ybbHu0',
          },
          timeout: 150000,
        }
      );

      setIsImageToStickerLoading(false);
      const prediction = response.data;
      console.log('Prediction:', prediction);

      // The Supabase Edge Function CAN return HTTP 200 with a body
      // that doesn't include image output (e.g. when Gemini refuses
      // the prompt, or when the function ran but produced no
      // candidates). Surface whatever message is in the response so
      // the user knows WHY no sticker came back instead of a vague
      // "No image generated."
      if (!prediction || !Array.isArray(prediction.output) || !prediction.output[0]) {
        const serverDetail =
          (typeof prediction === 'string' && prediction) ||
          prediction?.error ||
          prediction?.message ||
          prediction?.detail ||
          prediction?.geminiError ||
          (typeof prediction?.geminiResponse === 'string'
            ? prediction.geminiResponse
            : prediction?.geminiResponse?.error?.message) ||
          (prediction
            ? `Unexpected response from sticker service: ${JSON.stringify(prediction).slice(0, 300)}`
            : 'No response from sticker service.');
        console.error('Gemini sticker generation failed:', prediction);
        setImageToStickerErrorMessage(String(serverDetail));
        setBaseImageToStickerUrl('');
        setImageCropNotice('');
        return { original: '', transparent: '' };
      }

      // Extract the image URLs from the prediction object
      const originalImageUrl = prediction.output[0];
      const transparentImageUrl = prediction.output[1];

      console.log('Original Image URL:', originalImageUrl);
      console.log('Transparent Image URL:', transparentImageUrl);

      setBaseImageToStickerUrl(transparentImageUrl);
      const finalTransparent = await applyPostProcess('image', transparentImageUrl);

      return { original: originalImageUrl, transparent: finalTransparent };
    } catch (error) {
      setIsImageToStickerLoading(false);
      console.error('Error generating image to sticker:', error);
      if (axios.isAxiosError(error)) {
        const errData = error.response?.data as any;
        // Surface the most specific message the server provided —
        // including nested Gemini error structures.
        const serverMessage =
          (typeof errData === 'string' && errData) ||
          errData?.error ||
          errData?.message ||
          errData?.detail ||
          errData?.geminiError ||
          (typeof errData?.geminiResponse === 'string'
            ? errData.geminiResponse
            : errData?.geminiResponse?.error?.message) ||
          error.message;
        const status = error.response?.status;
        const prefix = status ? `(HTTP ${status}) ` : '';
        setImageToStickerErrorMessage(
          serverMessage
            ? `${prefix}${serverMessage}`
            : `${prefix}An unexpected error occurred.`
        );
      } else if (error instanceof Error) {
        setImageToStickerErrorMessage(error.message);
      } else {
        setImageToStickerErrorMessage('An unexpected error occurred.');
      }
      setBaseImageToStickerUrl('');
      setImageCropNotice('');
      return { original: '', transparent: '' };
    }
  };


const handleGenerateImageToSticker = async () => {
  if (!uploadedImage) {
    setImageToStickerErrorMessage('Please select an image file.');
    return;
  }

  try {
    setAddImageToStickerCollectionError('');
    setImageToStickerSuccessMessage('');
    setIsImageToStickerLoading(true);
    await dispatch(fetchMyCollection());
    // Assuming myCollectionItem is updated by the fetchMyCollection action
    const newCreditBalance = myCollectionItem ? (Array.isArray(myCollectionItem) ? myCollectionItem[0]?.credit_balance || 0 : myCollectionItem.credit_balance || 0) : 0;
    if (newCreditBalance < 20) {
      setImageToStickerErrorMessage('Insufficient credits. You need at least 20 credits to complete this transaction.');
      setIsImageToStickerLoading(false);
      return;
    }
    await processStickerGeneration();
    await processStickerGeneration(); // Deduct an additional 10 credits for a total of 20 credits
    const { original, transparent } = await generateImageToSticker(uploadedImage, imageToStickerPrompt);
    setImageToStickerUrl(transparent);
    setOriginalImageUrl(original);
    setIsImageToStickerLoading(false);
  } catch (error) {
    setIsImageToStickerLoading(false);
    console.error('Failed to process image to sticker generation:', error);
    if (error instanceof Error) {
      setImageToStickerErrorMessage(error.message);
    } else {
      setImageToStickerErrorMessage('An unexpected error occurred.');
    }
  }
};

const addImageToStickerToCollection = async () => {
  try {
    setIsLoading(true);
    setAddImageToStickerCollectionError('');
    setImageToStickerSuccessMessage('');
    await dispatch(fetchMyCollection()); // Fetch the latest collection to update the credit balance
    const updatedCreditBalance = myCollectionItem ? (Array.isArray(myCollectionItem) ? myCollectionItem[0]?.credit_balance || 0 : myCollectionItem.credit_balance || 0) : 0;
    if (updatedCreditBalance < 10) {
      throw new Error('Insufficient credits. You need at least 10 credits to complete this transaction.');
    }
    const token = localStorage.getItem('token');
    const productId = generateRandomId();
    const response = await axios.post(
      GET_BASE_URL + "/api/v1/addStickerFairytoCollection",
      {
        product_id: productId,
        product_name: imageToStickerProductName,
        product_image: imageToStickerThumbnailUrl,
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status === 200) {
      console.log('Image to Sticker added to collection successfully');
      setImageToStickerSuccessMessage('Your Sticker has been added to your collection. To add duplicates, press Add to Collection button above again!');
    } else {
      throw new Error('Failed to add Image to Sticker to collection');
    }
  } catch (error) {
    console.error('Error adding Image to Sticker to collection:', error);
    if (error instanceof Error) {
      setAddImageToStickerCollectionError(error.message);
    } else {
      setAddImageToStickerCollectionError('An unexpected error occurred.');
    }
  } finally {
    setIsLoading(false);
  }
};


  const renderImageCard = (
    title: string,
    url: string,
    downloadName: string,
    variant: 'default' | 'thumbnail' = 'default',
    actions?: React.ReactNode
  ) => {
    if (!url) {
      return null;
    }

    const cardClassName = ['generated-card'];
    if (variant === 'thumbnail') {
      cardClassName.push('generated-card--thumbnail');
    }

    return (
      <div className={cardClassName.join(' ')}>
        <h4>{title}</h4>
        <a
          href={url}
          download={downloadName}
          className="generated-card__download"
          onClick={(event) => {
            event.preventDefault();
            handleDownload(url, downloadName);
          }}
        >
          <img src={url} alt={title} />
          <span className="generated-card__hint">Tap to download</span>
        </a>
        {actions && <div className="generated-card__actions">{actions}</div>}
      </div>
    );
  };


return (
  <div className="sticker-fairy-page">
    <div className="sticker-fairy-hero">
      <img src={StickerFairyLogo} alt="Sticker Fairy" />
    </div>

    <section className="generator-section">
      <div className="generator-body">
        <div className="generator-input">
        <h3 className="generator-title">StickerFairy 3.0</h3>
          <input
            id="stickerPrompt"
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={'Describe your sticker, e.g. magical text sticker that says "HALLOWEENTIME at DISNEYLAND"'}
          />
          <button onClick={handleGenerateImage} disabled={isLoading}>
            Generate Sticker (10 Credits)
          </button>

          {errorMessage && <p className="feedback feedback--error">{errorMessage}</p>}

          {isLoading && (
            <div className="loading-container">
              <img src={dotsGif} alt="Loading" />
              <span>Generating your sticker...</span>
            </div>
          )}
        </div>

        <div className="generator-output">
          {!isLoading && (
            <>
              {renderImageCard(
                'Transparent sticker',
                transparentImageUrl,
                'transparent_sticker.png',
                'default',
                transparentImageUrl && (
                  <>
                    <div className="crop-actions">
                      <button type="button" onClick={() => openCropModal('text', transparentImageUrl)}>
                        Edit Crop
                      </button>
                      {baseTransparentUrl && (
                        <button type="button" onClick={() => handleResetCrop('text')}>
                          Reset Crop
                        </button>
                      )}
                    </div>
                    {textCropNotice && <p className="feedback feedback--error">{textCropNotice}</p>}
                  </>
                )
              )}
              {renderImageCard('Original artwork', originalImageUrl, 'original_sticker.png')}
              {renderImageCard('Thumbnail preview', thumbnailUrl, 'thumbnail_sticker.png', 'thumbnail')}

              {transparentImageUrl && (
                <div className="generated-card generated-card--action">
                  <h4>Name & add to collection</h4>
                  <input
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Name your sticker"
                  />
                  <button
                    onClick={() => {
                      addStickerFairyToCollection().catch((error) => {
                        console.error('Error adding Sticker Fairy to collection:', error);
                        if (error instanceof Error) {
                          setAddCollectionErrorMessage(error.message);
                        } else {
                          setAddCollectionErrorMessage('An unexpected error occurred.');
                        }
                      });
                    }}
                  >
                    Add to Collection (10 Credits each)
                  </button>
                  {addCollectionErrorMessage && <p className="feedback feedback--error">{addCollectionErrorMessage}</p>}
                  {successMessage && <p className="feedback feedback--success">{successMessage}</p>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>

    <section className="generator-section">
      <div className="generator-body">
        <div className="generator-input">
          <div className="file-input">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setUploadedImage(e.target.files[0]);
                }
              }}
            />
            {uploadedImage && <span className="file-name">{uploadedImage.name}</span>}
          </div>

          <input
            id="imageToStickerPrompt"
            type="text"
            value={imageToStickerPrompt}
            onChange={(e) => setImageToStickerPrompt(e.target.value)}
            placeholder={'Describe who you want to be, e.g. "Show me as a Pixar-style superhero standing in front of Sleeping Beauty Castle"'}
          />
          <button onClick={handleGenerateImageToSticker} disabled={isImageToStickerLoading}>
            Generate Image to Sticker (20 Credits)
          </button>

          {imageToStickerErrorMessage && <p className="feedback feedback--error">{imageToStickerErrorMessage}</p>}

          {isImageToStickerLoading && (
            <div className="loading-container">
              <img src={dotsGif} alt="Loading" />
              <span>Creating your transformation...</span>
            </div>
          )}
        </div>

        <div className="generator-output">
          {!isImageToStickerLoading && (
            <>
              {renderImageCard(
                'Sticker result',
                imageToStickerUrl,
                'sticker_upload.png',
                'default',
                imageToStickerUrl && (
                  <>
                    <div className="crop-actions">
                      <button type="button" onClick={() => openCropModal('image', imageToStickerUrl)}>
                        Edit Crop
                      </button>
                      {baseImageToStickerUrl && (
                        <button type="button" onClick={() => handleResetCrop('image')}>
                          Reset Crop
                        </button>
                      )}
                    </div>
                    {imageCropNotice && <p className="feedback feedback--error">{imageCropNotice}</p>}
                  </>
                )
              )}
              {renderImageCard('Original artwork', originalImageUrl, 'original_sticker.png')}
              {renderImageCard('Thumbnail preview', imageToStickerThumbnailUrl, 'thumbnail_sticker.png', 'thumbnail')}

              {imageToStickerUrl && (
                <div className="generated-card generated-card--action">
                  <h4>Name & add to collection</h4>
                  <input
                    type="text"
                    value={imageToStickerProductName}
                    onChange={(e) => setImageToStickerProductName(e.target.value)}
                    placeholder="Name your sticker"
                  />
                  <button
                    onClick={() => {
                      addImageToStickerToCollection().catch((error) => {
                        console.error('Error adding Image to Sticker to collection:', error);
                        if (error instanceof Error) {
                          setAddImageToStickerCollectionError(error.message);
                        } else {
                          setAddImageToStickerCollectionError('An unexpected error occurred.');
                        }
                      });
                    }}
                  >
                    Add to Collection (10 Credits each)
                  </button>
                  {imageToStickerSuccessMessage && <p className="feedback feedback--success">{imageToStickerSuccessMessage}</p>}
                  {addImageToStickerCollectionError && (
                    <p className="feedback feedback--error">{addImageToStickerCollectionError}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>

    <Modal
      isOpen={isCropping}
      onRequestClose={closeCropModal}
      contentLabel="Edit Crop"
      className="sticker-fairy-crop-modal"
      overlayClassName="sticker-fairy-crop-modal__overlay"
    >
      <div className="crop-modal-content">
        <h3>Edit Crop</h3>
        {cropSourceUrl && (
          <div className="crop-modal-canvas">
            <div className="crop-modal-cropper">
              <ReactCrop
                crop={crop}
                onChange={onCropChange}
                onComplete={onCropComplete}
              >
                <img
                  ref={cropImageRef}
                  src={cropSourceUrl}
                  alt="Crop preview"
                  onLoad={onCropImageLoad}
                />
              </ReactCrop>
            </div>
          </div>
        )}
        {cropError && <p className="crop-modal-error">{cropError}</p>}
        <div className="crop-modal-controls" aria-label="Crop adjustment controls">
          <div className="crop-modal-control-group crop-modal-control-group--move" aria-label="Move crop area">
            <button type="button" onClick={() => handleCropMove(0, -4)} aria-label="Move crop up">
              ↑
            </button>
            <button type="button" onClick={() => handleCropMove(-4, 0)} aria-label="Move crop left">
              ←
            </button>
            <button type="button" onClick={() => handleCropMove(4, 0)} aria-label="Move crop right">
              →
            </button>
            <button type="button" onClick={() => handleCropMove(0, 4)} aria-label="Move crop down">
              ↓
            </button>
          </div>
          <div className="crop-modal-control-group" aria-label="Resize crop area">
            <button type="button" onClick={() => handleCropResize(-6)}>
              Smaller
            </button>
            <button type="button" onClick={() => handleCropResize(6)}>
              Larger
            </button>
            <button type="button" onClick={handleCropResetSelection}>
              Reset
            </button>
          </div>
        </div>
        <div className="crop-modal-actions">
          <button type="button" onClick={handleCropApply}>
            Apply
          </button>
          <button type="button" onClick={closeCropModal}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  </div>
);
}

export default StickerFairy;
